'use client';
import React, { Suspense } from 'react';
import styles from './NewArticleLayoutHeader.module.scss';

import {
  ArticleData,
  authorTypeGenerator,
  calculateReadTime,
  Campaigns,
  isPaywallArticle,
  isProOnlyPost,
} from '@benzinga/article-manager';

import { useHydrate } from '@benzinga/hooks';
import { checkDeviceType, DeviceType } from '@benzinga/device-utils';
import { ErrorBoundary, EdgeBadge, ProBadge } from '@benzinga/core-ui';
import { truncate } from '@benzinga/utils';
import { IsUserPaywalledReturn, useIsUserTrialing, usePermission } from '@benzinga/user-context';
import Image from 'next/image';
import { NodeLayout } from '@benzinga/content-manager';

import { DateContent } from './DateContent';
import { ArticleFeaturedTicker } from '../entities';
import { NewAuthorContent } from './NewAuthorContent';
import { ArticleLayoutHeader as ArticleLayoutEditorBox } from './ArticleLayoutHeader';
import { AdvertiserDisclosure } from './AdvertiserDisclosure';
import { VideoArticlePlayer } from './VideoArticlePlayer';
import { getVideoArticleTitle, getVideoArticleYoutubeId } from '../utils/videoArticle';

export const ARTICLE_HEADER_BACKGROUND_IMAGE =
  'https://cdn.benzinga.com/files/images/story/2025/07/24/article-header-background-image.png';

const FlipboardButton = React.lazy(() => import('./FlipboardButton').then(m => ({ default: m.FlipboardButton })));
const GoogleNewsButton = React.lazy(() => import('./GoogleNewsButton').then(m => ({ default: m.GoogleNewsButton })));
const AppleNewsButton = React.lazy(() => import('./AppleNewsButton').then(m => ({ default: m.AppleNewsButton })));
const YouTubeFollowButton = React.lazy(() =>
  import('./YouTubeFollowButton').then(m => ({ default: m.YouTubeFollowButton })),
);
const ArticleFeaturedTickersList = React.lazy(() => import('./ArticleFeaturedTickersList'));

interface ArticleLayoutHeaderProps {
  articleData: ArticleData;
  campaigns?: Campaigns | null;
  deviceType: DeviceType | null;
  featuredTickers: ArticleFeaturedTicker[];
  googleNewsUrlKey: 'benzinga' | 'benzingaIndia';
  headerRef?: (node: HTMLDivElement | null) => void;
  hideTopPanel?: boolean;
  isBot: boolean;
  isDraft: boolean;
  isEditor: boolean;
  isTemplate?: boolean;
  isVideoArticle?: boolean;
  paywall: IsUserPaywalledReturn;
  showAdvertiserDisclosure: boolean;
  showFullProHeadline?: boolean;
  baseUrl?: string;
  layout?: NodeLayout | null;
}

export const NewArticleLayoutHeader: React.FC<ArticleLayoutHeaderProps> = React.memo(
  ({
    articleData,
    baseUrl,
    deviceType,
    featuredTickers,
    googleNewsUrlKey,
    headerRef,
    hideTopPanel = false,
    isDraft = false,
    isEditor,
    isTemplate = false,
    isVideoArticle = false,
    layout,
    showAdvertiserDisclosure = false,
    showFullProHeadline = false,
  }) => {
    const isApple = useHydrate(checkDeviceType().isApple(), false);
    const authorName =
      articleData?.author?.firstname && articleData?.author?.lastname
        ? `${articleData?.author.firstname} ${articleData?.author.lastname}`
        : articleData?.author?.name || articleData?.name;

    const createdAt = articleData.createdAt ? new Date(articleData.createdAt).toISOString() : null;
    const contentType = articleData?.type;
    const isProOnly = isProOnlyPost(articleData);

    const hasProDashboard = usePermission('bzpro/dashboard/open', '#');
    const isProTrialing = useIsUserTrialing('benzinga-pro');
    const isBenzingaProUser = hasProDashboard || isProTrialing;

    const showUnclippedHeadline = showFullProHeadline || isBenzingaProUser;

    const title = showUnclippedHeadline
      ? (articleData.title ?? '')
      : articleData?.isHeadline
        ? truncate(articleData.title ?? '', 45)
        : (articleData.title ?? '');

    const hasBadges = isProOnly || isPaywallArticle(articleData);
    const sidebarLgMinH = featuredTickers[0]?.type === 'CRYPTO' ? 'lg:min-h-[500px]' : 'lg:min-h-[600px]';
    const videoYoutubeId = isVideoArticle ? getVideoArticleYoutubeId(articleData) : null;
    const videoTitle = isVideoArticle ? getVideoArticleTitle(articleData) : '';

    return (
      <div
        className={`${styles.container}${isTemplate ? ` ${styles.isTemplate}` : ''}${isVideoArticle ? ` ${styles.isVideo}` : ''} article-layout-main-header flex flex-col`}
        ref={headerRef}
      >
        {!isTemplate && (
          <div className="relative z-20">
            <ArticleLayoutEditorBox
              articleData={articleData}
              baseUrl={baseUrl}
              isEditor={isEditor}
              isMobile={deviceType === 'mobile'}
              layout={layout}
            />
          </div>
        )}
        {!isTemplate && !isVideoArticle && (
          <span className="absolute inset-0 z-0 pointer-events-none">
            <Image
              alt=""
              fill
              fetchPriority="high"
              priority
              sizes={deviceType === 'mobile' ? '450px' : '1300px'}
              src={ARTICLE_HEADER_BACKGROUND_IMAGE}
              style={{ objectFit: 'cover' }}
            />
          </span>
        )}
        <div className="flex relative z-10 overflow-hidden">
          <div className="article-layout-main-header-content flex w-full gap-4 p-4">
            {isVideoArticle ? (
              <div className={`${styles.contentContainer} video-article-header flex w-full flex-col`}>
                <div className="content-wrapper video-article-meta">
                  <div className="flex flex-col w-full items-start lg:flex-row">
                    {hasBadges && (
                      <div className="badges">
                        {isProOnly ? (
                          <span>
                            <ProBadge />
                          </span>
                        ) : (
                          <span>{EdgeBadge}</span>
                        )}
                      </div>
                    )}
                    <ErrorBoundary name="article-layout-main-date-content">
                      <Suspense fallback={<div className="min-h-[26px] min-w-[320px]" />}>
                        <DateContent
                          created={createdAt ?? ''}
                          isDraft={isDraft}
                          readTime={calculateReadTime(articleData.body)}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  </div>
                  {!!showAdvertiserDisclosure && <AdvertiserDisclosure slug={layout?.disclosure_slug} />}

                  <h2 className="article-title text-2xl md:text-4xl text-white font-normal text-balance mt-2 mb-2">
                    {title}
                  </h2>

                  {!hideTopPanel && (
                    <div className="flex flex-col w-full items-start lg:flex-row min-h-[25px]">
                      <ErrorBoundary name="article-layout-main-author-content">
                        <Suspense fallback={<div />}>
                          <NewAuthorContent
                            authorLink={articleData.author?.profileUrl}
                            authorName={authorName}
                            authorType={authorTypeGenerator(
                              articleData.author?.byLine ?? '',
                              showAdvertiserDisclosure,
                              contentType,
                            )}
                            authorUid={articleData.author?.uid}
                            className="flex flex-grow"
                            isTemplate={isTemplate}
                            twitter={articleData.author?.twitter}
                          />
                        </Suspense>
                      </ErrorBoundary>
                    </div>
                  )}
                </div>

                <div className="video-article-media-row">
                  <div className="video-article-media min-w-0">
                    <div className="video-article-hero min-w-0">
                      <VideoArticlePlayer className="mb-0" priority title={videoTitle} youtubeId={videoYoutubeId} />
                    </div>

                    {articleData.title && (
                      <div
                        aria-label="Share"
                        className="share-buttons-row mt-3 flex h-[40px] min-w-[250px] w-full max-w-[460px] gap-2 items-center [&>*]:shrink-0"
                        role="group"
                      >
                        <Suspense fallback={<div aria-hidden className="h-[40px] w-[250px]" />}>
                          <YouTubeFollowButton variant="compact" />
                          <FlipboardButton />
                          <GoogleNewsButton urlKey={googleNewsUrlKey} />
                          {!showAdvertiserDisclosure && (
                            <span aria-hidden className="inline-flex h-[40px] w-[144px] shrink-0">
                              {isApple ? <AppleNewsButton /> : null}
                            </span>
                          )}
                        </Suspense>
                      </div>
                    )}
                  </div>

                  {Array.isArray(featuredTickers) && featuredTickers.length > 0 ? (
                    <aside className="video-article-ticker-sidebar">
                      <Suspense fallback={<div className="min-h-[280px] w-full rounded-md bg-[#0e1d32]" />}>
                        <ArticleFeaturedTickersList fillHeight tickers={featuredTickers} />
                      </Suspense>
                    </aside>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div className={`${styles.contentContainer} flex flex-col w-full h-full`}>
                  {articleData.image && (
                    <>
                      <Image
                        alt={articleData.assets?.[0]?.alt ?? 'Article image'}
                        className="absolute inset-0 w-full h-full z-0"
                        fill
                        fetchPriority="high"
                        priority
                        sizes={deviceType === 'mobile' ? '400px' : '850px'}
                        src={articleData.image}
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="absolute inset-0 z-[1] bg-[rgba(25,41,64,0.85)] opacity-75" />
                    </>
                  )}
                  <div className="content-wrapper">
                    <div className="flex flex-col w-full items-start lg:flex-row">
                      {hasBadges && (
                        <div className="badges">
                          {isProOnly ? (
                            <span>
                              <ProBadge />
                            </span>
                          ) : (
                            <span>{EdgeBadge}</span>
                          )}
                        </div>
                      )}
                      <ErrorBoundary name="article-layout-main-date-content">
                        <Suspense fallback={<div className="min-h-[26px] min-w-[320px]" />}>
                          <DateContent
                            created={createdAt ?? ''}
                            isDraft={isDraft}
                            readTime={calculateReadTime(articleData.body)}
                          />
                        </Suspense>
                      </ErrorBoundary>
                    </div>
                    {!!showAdvertiserDisclosure && <AdvertiserDisclosure slug={layout?.disclosure_slug} />}

                    <h2 className="article-title text-2xl md:text-4xl text-white font-bold my-4">{title}</h2>

                    {!hideTopPanel && (
                      <div className="flex flex-col w-full items-start lg:flex-row min-h-[25px]">
                        <ErrorBoundary name="article-layout-main-author-content">
                          <Suspense fallback={<div />}>
                            <NewAuthorContent
                              authorLink={articleData.author?.profileUrl}
                              authorName={authorName}
                              authorType={authorTypeGenerator(
                                articleData.author?.byLine ?? '',
                                showAdvertiserDisclosure,
                                contentType,
                              )}
                              authorUid={articleData.author?.uid}
                              className="flex flex-grow"
                              isTemplate={isTemplate}
                              twitter={articleData.author?.twitter}
                            />
                          </Suspense>
                        </ErrorBoundary>
                      </div>
                    )}

                    {articleData.title && (
                      <div
                        aria-label="Share"
                        className="share-buttons-row flex h-[40px] min-w-[250px] w-full mt-auto ml-auto gap-2 items-center [&>*]:shrink-0 max-w-[352px]"
                        role="group"
                      >
                        <Suspense fallback={<div aria-hidden className="h-[40px] w-[250px]" />}>
                          <FlipboardButton />
                          <GoogleNewsButton urlKey={googleNewsUrlKey} />
                          {!showAdvertiserDisclosure && (
                            <span aria-hidden className="inline-flex h-[40px] w-[144px] shrink-0">
                              {isApple ? <AppleNewsButton /> : null}
                            </span>
                          )}
                        </Suspense>
                      </div>
                    )}
                  </div>
                </div>

                {Array.isArray(featuredTickers) && featuredTickers.length > 0 ? (
                  <div
                    className={`article-layout-header-sidebar hidden md:flex flex-col gap-4 w-full max-w-[450px] min-h-0 ${sidebarLgMinH}`}
                  >
                    <Suspense fallback={<div className={`min-h-0 w-full ${sidebarLgMinH}`} />}>
                      <ArticleFeaturedTickersList tickers={featuredTickers} />
                    </Suspense>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

NewArticleLayoutHeader.displayName = 'NewArticleLayoutHeader';
